import { detect } from "detect-browser";
import BaseDialog from "../modelDialogs/BaseDialog";
import { exportProjectAsJSON } from "../export";
import { useEffect, useRef, useState } from "react";

import Logger from "../Logger";
import { showToast } from "./Toasts";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import type Device from "../models/Device";
import { askForGroupAddressUpdate } from "../addressGeneration/addressGenerator";
import BaseModel from "../models/BaseModel";

import { itx, noItx } from "../translationItx/itx";
import {
  closeLocalEtsWebSocket,
  closeEtsWebSocket,
  etsEventEmitter,
  etsWebSocket,
  openEtsWebSocket,
  EtsConnectionMode,
  etsConnectionMode,
  getActiveEtsSocket,
  localEtsWebSocket,
  setSelectedLocalEtsProjectId,
  setEtsConnectionMode,
} from "../etsFunctions/etsWebSocket";
import { isUiModeDeveloper, isUiModeProductAdmin } from "../uiMode";
import { ETS_MIN_VERSION, isVersionSufficient } from "../etsFunctions/etsVersionRequirements";

import { useRedraw } from "../models/useRerender";
import {
  checkIfEtsOnline,
  checkIfLocalEtsOnline,
  readbackFromETS,
  readbackFromJSON,
  syncWithETS,
  getReadbackSource,
  getLastReadbackSummary,
  getLastEtsPushDeveloperTiming,
  isReadbackActive,
  isSyncActive,
  wasLastEtsPushLocal,
  resetEtsOperationState,
  EtsLocalProject,
  showETSPushImportIssues,
} from "../etsFunctions/etsFunctions";
import type { EtsReadbackMode, EtsReadbackSource } from "../etsFunctions/etsFunctions";
import { EtsSyncConfig } from "../models/EtsSyncConfig";
import ExportEtsProjectViaServerButton from "./ExportEtsProjectViaServerButton";
import { ProjectLicenseType } from "../models/ProjectLicense";
import ToShopLicensesDialog from "../modelDialogs/ToShopLicensesDialog";
import EtsReadbackResultDialog from "../modelDialogs/EtsReadbackResultDialog";

const logger = new Logger();

type EtsDeviceDownloadType =
  | "All" //no itx
  | "Partial" //no itx
  | "LoadApplicationProgram" //no itx
  | "LoadNetworkConfiguration" //no itx
  | "LoadNetworkConfigurationBySerialNumber" //no itx
  | "OverwriteIndividualAddress"; //no itx

type EtsProjectDevice = {
  etsId?: string;
  name?: string;
  description?: string;
  physicalAddress: string;
  lineName?: string;
  lineAddress?: string;
  roomName?: string;
  productName?: string;
  orderNumber?: string;
  manufacturerName?: string;
  serialNumber?: string;
  downloadCounter?: number;
  lastDownload?: string;
  applicationProgramLoaded?: boolean;
  individualAddressLoaded?: boolean;
  parametersLoaded?: boolean;
  communicationPartLoaded?: boolean;
  mediumConfigLoaded?: boolean;
  connectionAvailable?: boolean;
};

type EtsDeviceStatus = {
  status?: string;
  physicalAddress?: string;
  existsInProject?: boolean;
  connectionAvailable?: boolean;
  busProbePerformed?: boolean;
  existsOnBus?: boolean;
  busConnectionInfo?: string;
  lastBusConnectionInfo?: string;
  message?: string;
  device?: EtsProjectDevice;
};

type EtsOnlineOperationEvent = {
  state?: string;
  eventKind?: string;
  operationType?: string;
  loadUnloadType?: string;
  physicalAddress?: string;
  deviceName?: string;
  progress?: number;
  message?: string;
  errorMessage?: string;
  requiresProgrammingMode?: boolean;
  isTerminal?: boolean;
  busConnectionInfo?: string;
};

type EtsBusInfo = {
  supported?: boolean;
  fallbackConnection?: string;
  lastBusConnectionInfo?: string;
  message?: string;
};

const DEVICE_DOWNLOAD_PREFERENCES_KEY = "estada_ets_device_download_preferences";
const LOCAL_ETS_PROBE_RETRY_MS = 5000;

function reopenEtsDialog() {
  state.dialogManager.openDialog(<EtsDialog />, true);
}

// ─── ETS Log Viewer Dialog ────────────────────────────────────────────────────

type EtsLogEntry = {
  Timestamp: string;
  Description: string;
};

function EtsLogViewerDialog(props: { etsIsOnline: boolean; etsVersion: string; onClose: () => false }) {
  const [logEntries, setLogEntries] = useState<EtsLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<"live" | "cached" | null>(null); //no itx
  const [filter, setFilter] = useState("");

  const canFetchLive = props.etsIsOnline && isVersionSufficient(props.etsVersion, ETS_MIN_VERSION.GET_ETS_LOG);

  function loadCached() {
    const cached: EtsLogEntry[] = (state.projectMetadata.etsLog || []).map((e: any) => ({
      Timestamp: e.Timestamp || "",
      Description: e.Description || "",
    }));
    setLogEntries(cached);
    setSource("cached"); //no itx
  }

  async function fetchLiveLog() {
    setIsLoading(true);
    setError(null);
    try {
      const res = await getActiveEtsSocket()?.sendCommandAsync("getEtsLog", {}, 15000);
      if (res?.status === "OK" && Array.isArray(res.data)) {
        setLogEntries(res.data as EtsLogEntry[]);
        setSource("live"); //no itx
      } else {
        setError(res?.message || itx("etsLogViewer.fetchError", "Failed to fetch log from ETS."));
        if (source !== "live") loadCached();
      }
    } catch (ex) {
      setError(itx("etsLogViewer.fetchError", "Failed to fetch log from ETS."));
      if (source !== "live") loadCached();
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    // Always load cached entries first so the table isn't blank during the live fetch
    loadCached();
    if (canFetchLive) {
      fetchLiveLog();
    }
  }, []);

  function formatTimestamp(ts: string): string {
    try {
      const d = new Date(ts);
      if (isNaN(d.getTime())) return ts;
      return d.toLocaleString();
    } catch {
      return ts;
    }
  }

  function getRowAccent(description: string): string {
    const lower = (description || "").toLowerCase();
    if (lower.includes("error") || lower.includes("fehler")) return "rgba(220,40,40,0.10)";
    if (lower.includes("warn") || lower.includes("warnung")) return "rgba(255,165,0,0.10)";
    return "";
  }

  const filtered = filter.trim()
    ? logEntries.filter(
        (e) =>
          e.Description.toLowerCase().includes(filter.toLowerCase()) ||
          e.Timestamp.toLowerCase().includes(filter.toLowerCase()),
      )
    : logEntries;

  return (
    <BaseDialog
      title={itx("etsLogViewer.title", "ETS Log History")}
      noOkButton={true}
      abortCaption={state.translate("close", "Close")}
      onClose={props.onClose}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          minWidth: "600px",
          maxWidth: "min(900px, calc(100vw - 120px))",
        }}
      >
        {/* ── Toolbar ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "8px",
            flexWrap: "wrap",
          }}
        >
          {/* Source badge */}
          {source && (
            <span
              style={{
                fontSize: "0.78em",
                padding: "2px 8px",
                borderRadius: "10px",
                background: source === "live" ? "rgba(76,175,80,0.15)" : "rgba(120,120,120,0.15)", //no itx
                border: source === "live" ? "1px solid rgba(76,175,80,0.5)" : "1px solid rgba(120,120,120,0.35)", //no itx
                color: source === "live" ? "var(--knxColorGreen, #4caf50)" : "inherit", //no itx
                flexShrink: 0,
              }}
            >
              {source === "live"
                ? itx("etsLogViewer.sourceLive", "Live from ETS")
                : itx("etsLogViewer.sourceCached", "Cached (last sync)")}
            </span>
          )}
          {/* Entry count */}
          {!isLoading && (
            <span style={{ fontSize: "0.82em", opacity: 0.65, flexShrink: 0 }}>
              {filter
                ? itx("etsLogViewer.filteredCount", `${filtered.length} / ${logEntries.length} entries`)
                : itx("etsLogViewer.totalCount", `${logEntries.length} entries`)}
            </span>
          )}
          {/* Spacer */}
          <div style={{ flex: 1 }} />
          {/* Filter input */}
          <input
            type="text"
            value={filter}
            onChange={(ev) => setFilter(ev.target.value)}
            placeholder={itx("etsLogViewer.filterPlaceholder", "Filter...")}
            style={{ width: "160px", fontSize: "0.88em" }}
          />
          {/* Refresh button — only when live fetch is possible */}
          {canFetchLive && (
            <button
              className="button"
              style={{ flexShrink: 0, paddingLeft: "8px", paddingRight: "8px" }}
              disabled={isLoading}
              title={itx("etsLogViewer.refresh", "Refresh from ETS")}
              onClick={fetchLiveLog}
            >
              <i className={`las la-sync${isLoading ? " la-spin" : ""}`} style={{ marginRight: "4px" }} />
              {itx("etsLogViewer.refresh", "Refresh")}
            </button>
          )}
          {/* Back to ETS dialog */}
          <button
            className="button"
            style={{ flexShrink: 0, paddingLeft: "8px", paddingRight: "8px" }}
            title={itx("etsLogViewer.backToEts", "Back to ETS dialog")}
            onClick={() => reopenEtsDialog()}
          >
            <i className="las la-arrow-left" style={{ marginRight: "4px" }} />
            {itx("etsLogViewer.backToEts", "Back to ETS")}
          </button>
        </div>

        {/* ── Version warning for live fetch ── */}
        {props.etsIsOnline && !canFetchLive && props.etsVersion && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              background: "rgba(255,165,0,0.12)",
              border: "1px solid orange",
              borderRadius: "4px",
              padding: "4px 10px",
              marginBottom: "8px",
              fontSize: "0.82em",
              color: "orange",
            }}
          >
            <i className="las la-exclamation-triangle" style={{ flexShrink: 0 }} />
            {itx(
              "etsLogViewer.versionTooLow",
              `Live ETS log requires plugin v${ETS_MIN_VERSION.GET_ETS_LOG}+. Connected: v${props.etsVersion}. Showing cached log.`,
            )}
          </div>
        )}

        {/* ── Error banner ── */}
        {error && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              background: "rgba(220,40,40,0.10)",
              border: "1px solid rgba(200,0,0,0.35)",
              borderRadius: "4px",
              padding: "4px 10px",
              marginBottom: "8px",
              fontSize: "0.82em",
              color: "rgba(200,0,0,0.9)",
            }}
          >
            <i className="las la-exclamation-circle" style={{ flexShrink: 0 }} />
            {error}
          </div>
        )}

        {/* ── Loading spinner ── */}
        {isLoading && (
          <div style={{ textAlign: "center", padding: "24px", opacity: 0.65 }}>
            <i className="las la-sync la-spin" style={{ fontSize: "22px", marginRight: "8px" }} />
            {itx("etsLogViewer.loading", "Loading ETS log...")}
          </div>
        )}

        {/* ── Table ── */}
        {!isLoading && (
          <div
            style={{ overflowY: "auto", maxHeight: "60vh", borderRadius: "4px", border: "1px solid rgba(0,0,0,0.12)" }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "0.88em",
              }}
            >
              <thead>
                <tr
                  style={{
                    color: "var(--knxColorWhite)",
                    backgroundColor: "var(--knxColorGrey)",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  <th
                    style={{
                      width: "200px",
                      padding: "7px 10px",
                      textAlign: "left",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {state.translate("menuBar.timestamp", "Timestamp")}
                  </th>
                  <th style={{ padding: "7px 10px", textAlign: "left", fontWeight: 600 }}>
                    {state.translate("description", "Description")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={2} style={{ textAlign: "center", padding: "24px", opacity: 0.55 }}>
                      {filter
                        ? itx("etsLogViewer.noMatchingEntries", "No entries match the filter.")
                        : itx("menuBar.noLogEntriesTodisplay", "No log entries to display.")}
                    </td>
                  </tr>
                )}
                {filtered.map((entry, index) => (
                  <tr
                    key={index}
                    style={{
                      backgroundColor:
                        getRowAccent(entry.Description) || (index % 2 === 0 ? "transparent" : "rgba(0,0,0,0.03)"),
                      borderBottom: "1px solid rgba(0,0,0,0.06)",
                    }}
                  >
                    <td
                      style={{
                        padding: "5px 10px",
                        whiteSpace: "nowrap",
                        verticalAlign: "top",
                        fontFamily: "monospace",
                        fontSize: "0.92em",
                        opacity: 0.8,
                      }}
                    >
                      {formatTimestamp(entry.Timestamp)}
                    </td>
                    <td style={{ padding: "5px 10px", verticalAlign: "top", wordBreak: "break-word" }}>
                      {entry.Description}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </BaseDialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

type EtsDialogProps = {
  autoPasteLastPinOnOpen?: boolean;
};

const ETS_PARAM_TEXT_IMPORT_MODE_KEY = "estada_ets_param_text_import_mode";
const SHOW_ETS_PARAM_TEXT_IMPORT_MODE_SWITCH = true;

export default function EtsDialog(props: EtsDialogProps = {}) {
  const redraw = useRedraw();

  const [onlyOne, setOnlyOne] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<string | undefined>(undefined);
  const [initialStatusChecked, setInitialStatusChecked] = useState(false);
  const [isSyncInProgress, setIsSyncInProgress] = useState(isSyncActive);
  const [isReadbackInProgress, setIsReadbackInProgress] = useState(isReadbackActive);
  const [readbackSource, setReadbackSource] = useState<EtsReadbackSource | undefined>(getReadbackSource);
  const [lastPushDeveloperTiming, setLastPushDeveloperTiming] = useState<any>(() => getLastEtsPushDeveloperTiming());
  const [etsVersion, setEtsVersion] = useState<string>("");
  const [etsBuildDate, setEtsBuildDate] = useState<string>("");
  const [etsProjectName, setEtsProjectName] = useState<string>("");
  const [localEtsProjects, setLocalEtsProjects] = useState<EtsLocalProject[]>([]);
  const [selectedLocalProjectId, setSelectedLocalProjectId] = useState<string>("");
  const [showMoreButtons, setShowMoreButtons] = useState<boolean>(false);
  const [paramTextImportMode, setParamTextImportMode] = useState<"fast" | "complete">("fast");
  const [lastUsedPin, setLastUsedPin] = useState<string>(() => localStorage.getItem("estada_ets_last_pin") || "");
  const [pinInputValue, setPinInputValue] = useState<string>(() =>
    wasLastEtsPushLocal() ? "" : state.projectMetadata.etsPin || localStorage.getItem("estada_ets_last_pin") || "",
  );
  const effectiveEtsPin = normalizeEtsPin(state.projectMetadata.etsPin || pinInputValue);
  const hasUsableEtsPin = isUsableEtsPin(effectiveEtsPin);
  const [deviceProgramAddress, setDeviceProgramAddress] = useState<string>("");
  const [deviceProgramCurrentAddress, setDeviceProgramCurrentAddress] = useState<string>("");
  const [deviceProgramDownloadType, setDeviceProgramDownloadType] = useState<EtsDeviceDownloadType>("All");
  const [projectDevices, setProjectDevices] = useState<EtsProjectDevice[]>([]);
  const [isLoadingProjectDevices, setIsLoadingProjectDevices] = useState<boolean>(false);
  const [isCheckingDeviceStatus, setIsCheckingDeviceStatus] = useState<boolean>(false);
  const [isProgrammingDevice, setIsProgrammingDevice] = useState<boolean>(false);
  const [isSettingProgrammingMode, setIsSettingProgrammingMode] = useState<boolean>(false);
  const [deviceProgramResult, setDeviceProgramResult] = useState<{ status: string; message: string } | null>(null);
  const [deviceProgramProgress, setDeviceProgramProgress] = useState<EtsOnlineOperationEvent | null>(null);
  const [deviceStatus, setDeviceStatus] = useState<EtsDeviceStatus | null>(null);
  const [busInfo, setBusInfo] = useState<EtsBusInfo | null>(null);
  const [isLoadingBusInfo, setIsLoadingBusInfo] = useState<boolean>(false);
  // Tracks whether ETS is reachable via cloud relay or directly via local WebSocket (offline mode)
  const [connectionMode, setConnectionMode] = useState<EtsConnectionMode>(etsConnectionMode); // no itx

  // Ref that always holds the last-known plugin version, used inside setInterval callbacks
  // to avoid stale-closure reads of the `etsVersion` state variable.
  const etsVersionRef = useRef<string>("");
  const connectionModeRef = useRef<EtsConnectionMode>(etsConnectionMode); // no itx
  const lastLocalEtsProbeFailedAtRef = useRef<number>(0);
  const relayPinPreferredUntilRef = useRef<number>(0);
  const offlineTransitionRecheckPendingRef = useRef<boolean>(false);

  const pinRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingReadbackFileModeRef = useRef<EtsReadbackMode>("full"); // no itx

  function supportsLocalEtsWebSocket(version = etsVersionRef.current): boolean {
    return Boolean(version) && isVersionSufficient(version, ETS_MIN_VERSION.LOCAL_WS);
  }

  function applyConnectionMode(mode: EtsConnectionMode) {
    connectionModeRef.current = mode;
    setEtsConnectionMode(mode); // no itx
    setConnectionMode(mode); // no itx
  }

  function clearPinAfterLocalPush() {
    relayPinPreferredUntilRef.current = 0;
    if (pinInputValue) {
      setPinInputValue("");
    }
    if (state.projectMetadata.etsPin) {
      state.projectMetadata.etsPin = "";
      state.projectMetadata.raiseOnChange("etsPin");
    }
    closeEtsWebSocket();
  }

  function shouldProbeLocalEtsWebSocket(force = false): boolean {
    if (force) return true;
    const lastKnownVersion = etsVersionRef.current;
    if (lastKnownVersion && !supportsLocalEtsWebSocket(lastKnownVersion)) return false;
    if (connectionModeRef.current === "local") return true;
    return Date.now() - lastLocalEtsProbeFailedAtRef.current >= LOCAL_ETS_PROBE_RETRY_MS;
  }

  function rememberLocalEtsProbe(status: boolean) {
    lastLocalEtsProbeFailedAtRef.current = status ? 0 : Date.now();
  }

  function beginEtsStatusCheck() {
    setInitialStatusChecked(false);
  }

  function scheduleEtsStatusCheck() {
    beginEtsStatusCheck();
    setTimeout(() => etsPingOnce(true), 0);
  }

  function applyLocalProjectSelection(data: any) {
    const hasProjectList = Array.isArray(data?.projects);
    const projects: EtsLocalProject[] = hasProjectList ? data.projects : localEtsProjects;
    if (hasProjectList) {
      setLocalEtsProjects(projects);
    }

    const validProjectIds = new Set(projects.map((project) => project.projectId).filter(Boolean));
    let nextProjectId = data?.selectedProjectId || data?.projectId || selectedLocalProjectId;
    if (projects.length === 1) {
      nextProjectId = projects[0].projectId;
    } else if (hasProjectList && nextProjectId && !validProjectIds.has(nextProjectId)) {
      nextProjectId = "";
    }
    if (hasProjectList && projects.length > 1 && !nextProjectId) {
      nextProjectId = projects.find((project) => project.selected)?.projectId || projects[0]?.projectId || "";
    }

    setSelectedLocalProjectId(nextProjectId || "");
    setSelectedLocalEtsProjectId(nextProjectId || "");

    const selectedProject =
      projects.find((project) => project.projectId === nextProjectId) ||
      projects.find((project) => project.selected) ||
      undefined;
    return selectedProject;
  }

  async function selectLocalProject(projectId: string) {
    setSelectedLocalProjectId(projectId);
    setSelectedLocalEtsProjectId(projectId);
    const selectedProject = localEtsProjects.find((project) => project.projectId === projectId);
    setEtsProjectName(selectedProject?.projectName || "");
    beginEtsStatusCheck();
    await localEtsWebSocket?.sendCommandAsync("selectLocalProject", { projectId }, 8000);
    setTimeout(() => etsPingOnce(true), 0);
  }

  const dataTooltipContent = itx(
    "etsDialog.connectionStatusInfo",
    "Um dein Projekt zu ETS zu pushen benötigst du die ESTADA ETS App. Wenn diese installiert wurde, und in einem Projekt als Panel geöffnet ist, wird der Status auf online gestellt",
  );

  useOnChangeWatcher(state.selectionManager);
  useOnChangeWatcher(state.projectMetadata);

  useEffect(() => {
    const syncInProgressHandler = (status: boolean) => {
      const active = Boolean(status);
      setIsSyncInProgress(active);
      if (!active) {
        scheduleEtsStatusCheck();
      }
    };
    const readbackInProgressHandler = (status: boolean, source?: EtsReadbackSource) => {
      const active = Boolean(status);
      setIsReadbackInProgress(active);
      setReadbackSource(active ? source || getReadbackSource() : undefined);
      if (!active) {
        scheduleEtsStatusCheck();
      }
    };
    const pushDeveloperTimingHandler = (timing: any) => {
      setLastPushDeveloperTiming(timing);
    };
    etsEventEmitter.on("syncProgressChange", syncInProgressHandler); // no itx
    etsEventEmitter.on("readbackProgressChange", readbackInProgressHandler); // no itx
    etsEventEmitter.on("pushDeveloperTimingChange", pushDeveloperTimingHandler); // no itx
    const operationActiveOnOpen = isSyncActive() || isReadbackActive();
    setIsSyncInProgress(isSyncActive());
    setIsReadbackInProgress(isReadbackActive());
    setReadbackSource(isReadbackActive() ? getReadbackSource() : undefined);
    setLastPushDeveloperTiming(getLastEtsPushDeveloperTiming());

    let id: any | undefined;

    if (wasLastEtsPushLocal()) {
      clearPinAfterLocalPush();
    }

    if (state.projectMetadata.etsPin && state.projectMetadata.etsPin.length >= 4 && !operationActiveOnOpen) {
      // Don't reopen the WebSocket if a push is already in progress — that
      // would disrupt the ongoing upload and clear the PIN on reconnect.
      openEtsWebSocket(state.projectMetadata.etsPin); // no itx
    }

    // Always ping on mount and keep polling — local mode works without a PIN
    if (operationActiveOnOpen) {
      setInitialStatusChecked(true);
    } else {
      etsPingOnce(true);
      id = setInterval(etsPingOnce, 5000);
    }

    // Listen for the "projectOpened" push event sent by the C# plugin (v1.7+) when a new ETS6
    // project is opened. Only set up when the plugin version is known to support local WS.
    // This is managed in the separate version-gated useEffect below.

    //initCatalog(true);

    return () => {
      clearInterval(id);
      if (!isSyncActive() && !isReadbackActive()) {
        etsWebSocket?.removeCallback("pingResult"); // no itx
        localEtsWebSocket?.removeCallback("pingResult"); // no itx (cleanup if local was opened)
      }
      etsEventEmitter.off("syncProgressChange", syncInProgressHandler); // no itx
      etsEventEmitter.off("readbackProgressChange", readbackInProgressHandler); // no itx
      etsEventEmitter.off("pushDeveloperTimingChange", pushDeveloperTimingHandler); // no itx

      //
      // MWE 12.12.2025
      //
      // wir dürfen den WebSocket hier nicht schließen,
      // da da Panel während unterschiedlicher Dialoge wären zB der GruppenAddress-Generierung
      // geschlossen wird
      //
      // closeEtsWebSocket();
      //
    };
  }, []);

  // Subscribe to local push events only while local mode is actually active. Do not open the
  // local socket here; it is probed lazily as priority 3 after relay checks fail.
  useEffect(() => {
    if (connectionMode !== "local" || !supportsLocalEtsWebSocket(etsVersion)) return;

    const handleProjectOpened = (data: any) => {
      if (data?.status === true) {
        const selectedProject = applyLocalProjectSelection(data);
        applyConnectionMode("local"); // no itx
        setEtsVersion(data.version || "");
        setEtsBuildDate(data.buildDate || "");
        setEtsProjectName(selectedProject?.projectName || data.projectName || "");
        etsVersionRef.current = data.version || "";
        rememberLocalEtsProbe(true);
        if (!state.etsIsOnline) {
          state.etsIsOnline = true;
          redraw();
        }
        setInitialStatusChecked(true);
      }
    };

    localEtsWebSocket?.on("projectOpened", handleProjectOpened); // no itx
    return () => {
      localEtsWebSocket?.off("projectOpened", handleProjectOpened); // no itx
    };
  }, [connectionMode, etsVersion]); // re-evaluates whenever version/mode is updated

  useEffect(() => {
    if (isSyncInProgress || isReadbackInProgress) return;
    if (
      !initialStatusChecked ||
      !state.etsIsOnline ||
      !isUiModeDeveloper() ||
      !isVersionSufficient(etsVersion, ETS_MIN_VERSION.PROGRAM_DEVICE)
    ) {
      setProjectDevices([]);
      return;
    }

    loadProjectDevicesForProgramming();
  }, [initialStatusChecked, connectionMode, etsVersion, isSyncInProgress, isReadbackInProgress]);

  useEffect(() => {
    // listBusInterfaces is a new-plugin (v1.7+) command — skip for old plugin to avoid a 30 s timeout wait
    if (isSyncInProgress || isReadbackInProgress) return;
    if (
      !initialStatusChecked ||
      !state.etsIsOnline ||
      !isUiModeDeveloper() ||
      !isVersionSufficient(etsVersion, ETS_MIN_VERSION.LOCAL_WS)
    )
      return;
    loadBusInfo();
  }, [initialStatusChecked, connectionMode, etsVersion, isSyncInProgress, isReadbackInProgress]);

  useEffect(() => {
    // etsOnlineOperation is a push event only supported by plugin v1.7+ — skip registration for older plugins
    if (!isVersionSufficient(etsVersion, ETS_MIN_VERSION.LOCAL_WS)) return;
    const socket = getActiveEtsSocket();
    if (!socket) return;

    const handleOnlineOperation = (data: EtsOnlineOperationEvent) => {
      applyOnlineOperationFeedback(data, "event"); // no itx
    };

    socket.on(
      "etsOnlineOperation", // no itx
      handleOnlineOperation,
    );
    return () =>
      socket.off(
        "etsOnlineOperation", // no itx
        handleOnlineOperation,
      );
  }, [connectionMode, deviceProgramAddress, etsVersion]);

  useEffect(() => {
    if (!isProgrammingDevice || validatePhysicalAddress(deviceProgramAddress)) return;

    let disposed = false;
    const pollOnlineOperationStatus = async () => {
      try {
        const res = await getActiveEtsSocket()?.sendCommandAsync(
          "getOnlineOperationStatus", // no itx
          { physicalAddress: deviceProgramAddress.trim() },
          8000,
        );
        if (disposed) return;
        if (res?.status === "OK" && res?.hasOperation && res?.operation) {
          applyOnlineOperationFeedback(res.operation, "poll"); // no itx
        }
      } catch (ex) {
        logger.error("Error polling ETS online operation status:", ex);
      }
    };

    pollOnlineOperationStatus();
    const pollId = setInterval(pollOnlineOperationStatus, 2000);
    const watchdogId = setTimeout(
      () => {
        if (disposed) return;
        setIsProgrammingDevice(false);
        setDeviceProgramResult({
          status: "ERROR",
          message: itx(
            "etsDialog.programDeviceNoCompletionFeedback",
            "No ETS completion feedback was received. Check the ETS pending operations window for the final result.",
          ),
        });
      },
      15 * 60 * 1000,
    );

    return () => {
      disposed = true;
      clearInterval(pollId);
      clearTimeout(watchdogId);
    };
  }, [isProgrammingDevice, deviceProgramAddress, connectionMode]);

  async function etsPingOnce(initRun?: boolean) {
    if (isSyncActive() || isReadbackActive()) {
      setInitialStatusChecked(true);
      return;
    }

    const preferRelayPin = Date.now() < relayPinPreferredUntilRef.current;
    const preferLocal = !preferRelayPin && connectionModeRef.current === "local";
    const forceLocalProbe = Boolean(initRun);
    const canProbeLocal = !preferRelayPin && shouldProbeLocalEtsWebSocket(forceLocalProbe);
    const shouldTryLocalFirst = canProbeLocal && !hasUsableEtsPin && preferLocal;
    let localStatus: {
      status: boolean;
      version: string;
      buildDate: string;
      projectName?: string;
      selectedProjectId?: string;
      projects?: EtsLocalProject[];
    } = {
      status: false,
      version: "",
      buildDate: "",
    };
    let localChecked = false;
    if (shouldTryLocalFirst) {
      // Keep local mode sticky while reconnecting after a local push, and avoid waiting
      // behind relay timeouts when localhost is the likely active ETS connection.
      localStatus = await checkIfLocalEtsOnline();
      rememberLocalEtsProbe(localStatus.status);
      localChecked = true;
    }

    // Priority: PIN/relay first. Local ETS is only used when no PIN is configured,
    // or when the PIN relay check fails.
    const relayStatus: Awaited<ReturnType<typeof checkIfEtsOnline>> = localStatus.status
      ? { status: false, pin: "", version: "", buildDate: "" }
      : hasUsableEtsPin
        ? await checkIfEtsOnline()
        : { status: false, pin: "", version: "", buildDate: "" };

    if (!localChecked && !relayStatus.status && shouldProbeLocalEtsWebSocket(forceLocalProbe)) {
      // Fallback local probe for relay-first cases.
      localStatus = await checkIfLocalEtsOnline();
      rememberLocalEtsProbe(localStatus.status);
    }

    let newStatus: { status: boolean; pin: string; version: string; buildDate: string; projectName?: string };

    if (relayStatus.status) {
      // Reachable via cloud relay — backward-compatible path (old plugin uses PIN + relay)
      closeLocalEtsWebSocket();
      rememberLocalEtsProbe(true);
      offlineTransitionRecheckPendingRef.current = false;
      applyConnectionMode("relay"); // no itx
      newStatus = relayStatus;
    } else if (localStatus.status) {
      // Local ETS6 server reachable (offline / same-machine mode, new plugin)
      const selectedProject = applyLocalProjectSelection(localStatus);
      offlineTransitionRecheckPendingRef.current = false;
      applyConnectionMode("local"); // no itx
      newStatus = {
        status: true,
        pin: "",
        version: localStatus.version,
        buildDate: localStatus.buildDate,
        projectName: selectedProject?.projectName || localStatus.projectName,
      };
    } else {
      if (state.etsIsOnline && !offlineTransitionRecheckPendingRef.current) {
        offlineTransitionRecheckPendingRef.current = true;
        beginEtsStatusCheck();
        setTimeout(() => etsPingOnce(true), 1000);
        return;
      }
      offlineTransitionRecheckPendingRef.current = false;
      setLocalEtsProjects([]);
      setSelectedLocalProjectId("");
      setSelectedLocalEtsProjectId("");
      if (!supportsLocalEtsWebSocket()) {
        closeLocalEtsWebSocket();
      }
      applyConnectionMode("offline"); // no itx
      newStatus = { status: false, pin: "", version: "", buildDate: "" };
    }

    if (newStatus.status) {
      setEtsVersion(newStatus.version);
      setEtsBuildDate(newStatus.buildDate);
      setEtsProjectName(newStatus.projectName || "");
      etsVersionRef.current = newStatus.version; // keep ref in sync so setInterval closure stays current
    } else {
      setEtsVersion("");
      setEtsBuildDate("");
      setEtsProjectName("");
      // intentionally do NOT clear etsVersionRef — keep last known version so subsequent
      // polls on old plugin don't retry the local-WS check after a transient relay miss
    }

    if (newStatus.status != state.etsIsOnline) {
      state.etsIsOnline = newStatus.status; // Used by DeviceTreeViewModel
      if (newStatus.status) {
        try {
          document.getElementsByClassName("la-sync")[0]?.classList.remove("la-spin");
        } catch (ex) {
          console.error(ex);
        }
      }
      redraw();
    }

    setInitialStatusChecked(true);

    /////////////////////////////////////////////////////////////////////////////////
    //
    // MWE 11.12.2025 disable auto pin set from ets app for now
    //
    // if (
    //   state.projectMetadata.etsPin === "" &&
    //   newStatus.pin &&
    //   newStatus.pin !== state.projectMetadata.etsPin &&
    //   initRun
    // ) {
    //   state.projectMetadata.etsPin = newStatus.pin;
    //   state.projectMetadata.raiseOnChange("etsPin");
    //   showToast({
    //     text:
    //       itx("etsDialog.pinFromEtsAppDetected", "ETS App Pin erkannt.\n Verbindung mit ETS wird aufgebaut. Pin:") +
    //       newStatus.pin,
    //   });

    //   closeEtsWebSocket();
    //   openEtsWebSocket(newStatus.pin || "ip*");
    // }
    //
    /////////////////////////////////////////////////////////////////////////////////
  }

  function applyPin(pin: string, preferRelay = true) {
    const p = normalizeEtsPin(pin);
    setPinInputValue(p);
    if (isUsableEtsPin(p) || p.length === 0) {
      beginEtsStatusCheck();
      state.projectMetadata.etsPin = p;
      closeEtsWebSocket();
      state.projectMetadata.raiseOnChange("etsPin");
      if (p) {
        relayPinPreferredUntilRef.current = preferRelay ? Date.now() + 8000 : 0;
        if (preferRelay) {
          closeLocalEtsWebSocket();
          setSelectedLocalEtsProjectId("");
          setSelectedLocalProjectId("");
          setLocalEtsProjects([]);
          setEtsProjectName("");
          applyConnectionMode("relay"); // no itx
        }
        openEtsWebSocket(p);
        scheduleEtsStatusCheck();
        setLastUsedPin(p);
        localStorage.setItem("estada_ets_last_pin", p);
      } else {
        relayPinPreferredUntilRef.current = 0;
        setEtsProjectName("");
        scheduleEtsStatusCheck();
      }
    }
  }

  function normalizeEtsPin(pin?: string) {
    return (pin || "").toUpperCase().trim();
  }

  function isUsableEtsPin(pin: string) {
    return pin.length === 4 || pin.length === 6;
  }

  function rememberEtsPin(pin: string) {
    if (!isUsableEtsPin(pin)) return;
    setLastUsedPin(pin);
    localStorage.setItem("estada_ets_last_pin", pin);
  }

  function ensureEtsPinApplied(pin: string) {
    const p = normalizeEtsPin(pin);
    if (isUsableEtsPin(p) && state.projectMetadata.etsPin !== p) {
      applyPin(p);
    }
    return p;
  }

  function getEffectiveParamTextImportMode(): "fast" | "complete" {
    return isUiModeDeveloper() && SHOW_ETS_PARAM_TEXT_IMPORT_MODE_SWITCH ? paramTextImportMode : "fast";
  }

  function updateParamTextImportMode(mode: "fast" | "complete") {
    setParamTextImportMode(mode);
    localStorage.setItem(ETS_PARAM_TEXT_IMPORT_MODE_KEY, mode);
  }

  useEffect(() => {
    if (wasLastEtsPushLocal()) {
      clearPinAfterLocalPush();
      return;
    }

    const pinToApply = state.projectMetadata.etsPin || lastUsedPin;
    if ((props.autoPasteLastPinOnOpen || !state.projectMetadata.etsPin) && pinToApply) {
      applyPin(pinToApply, false);
    }
  }, []);

  function getConfig(): EtsSyncConfig | undefined {
    let config: EtsSyncConfig | undefined;
    const effectiveParamTextImportMode = getEffectiveParamTextImportMode();

    if (onlyOne) {
      const obj = state.objectRegistry.getObject(selectedDevice);
      if (obj instanceof BaseModel && obj?.getIsDevice()) {
        config = {
          syncGroupAddresses: true,
          syncBuildingStructure: true,
          syncAllDevcies: false,
          devicesToSync: new Array<Device>(obj as Device),
          paramTextImportMode: effectiveParamTextImportMode,
        };

        return config;
      }
    }

    if (isUiModeDeveloper()) {
      return {
        syncGroupAddresses: true,
        syncBuildingStructure: true,
        syncAllDevcies: true,
        paramTextImportMode: effectiveParamTextImportMode,
      };
    }
  }

  async function handleImportReadbackFile(mode: EtsReadbackMode) {
    pendingReadbackFileModeRef.current = mode;
    fileInputRef.current?.click();
  }

  async function processReadbackFile(file: File) {
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      state.dialogManager.closeDialog();
      await readbackFromJSON(data, pendingReadbackFileModeRef.current);
    } catch (error) {
      logger.error("Error processing readback file:", error);
      showToast({
        text: itx("etsSyncAndExport.readbackError", "Error importing readback data"),
      });
    }
  }

  async function onClickExport() {
    await exportProjectAsJSON(getConfig());
    reopenEtsDialog();
  }

  function openEtsLogViewer() {
    state.dialogManager.openDialog(
      <EtsLogViewerDialog
        etsIsOnline={state.etsIsOnline}
        etsVersion={etsVersionRef.current}
        onClose={() => {
          reopenEtsDialog();
          return false;
        }}
      />,
    );
  }

  function OnlyOneSelectElm(props: {}) {
    let optionsElm = new Array<any>();
    let selectedObjId: undefined | string = selectedDevice;
    if (selectedObjId == undefined) {
      const obj = state.selectionManager.selectedObject;
      if (obj instanceof BaseModel && obj.getIsDevice()) {
        setSelectedDevice(obj.id);
        selectedObjId = obj.id;
      }
    }
    const options = state.allDevices();

    options.forEach((dev) => {
      if (dev.template) {
        if (selectedObjId == undefined && optionsElm.length == 0) {
          setSelectedDevice(dev.id);
          selectedObjId = dev.id;
        }
        const iconUrlStyle = { height: "22px" };
        const iconUrl = dev.treeViewModel?.iconUrl;
        const icon = (
          <div>
            {iconUrl?.substring(0, 2) == "la" && (
              <div className="icon" style={iconUrlStyle}>
                <i className={iconUrl}></i>
              </div>
            )}
            {iconUrl && iconUrl?.substring(0, 2) != "la" && (
              <img className="icon" src={iconUrl} draggable={false} style={iconUrlStyle} />
            )}
          </div>
        );
        optionsElm.push(
          <option value={dev.id}>
            {icon} {dev.fullTag} {dev.name}
          </option>,
        );
      }
    });

    return (
      <select
        className="select"
        id={`EtsSyncAndExport_onlyOne`}
        onChange={(ev) => {
          setSelectedDevice(ev.target.value);
        }}
        defaultValue={selectedObjId}
        style={{ width: "80%", margin: "auto", marginTop: "7px" }}
      >
        {optionsElm}
      </select>
    );
  }

  function EtsApplicationConnectionSpan(props: {}) {
    const generalStyle = { paddingRight: "15px" };
    return !initialStatusChecked ? (
      <span className="statusContainer initial" style={generalStyle}>
        <span className="statusDot initial" title={itx("etsSyncAndExport.checking", "Checking...")}></span>
        &nbsp; {state.translate("etsSyncAndExport.checking", "Checking")}...
      </span>
    ) : isEtsPluginOperationInProgress ? (
      <span className="statusContainer busy" style={generalStyle}>
        <span className="statusDot busy" title={itx("etsSyncAndExport.busy", "Busy")}></span> &nbsp;{" "}
        {state.translate("etsSyncAndExport.busy", "Busy")}
      </span>
    ) : // <span className="statusContainer idle">
    //   <span className="statusDot idle" title={itx("EtsSyncAndExport.idle", "Idle")}></span>
    //   &nbsp; {state.translate("etsSyncAndExport.idle", "Idle")}
    // </span>
    state.etsIsOnline ? (
      <span className="statusContainer online" style={generalStyle}>
        <span className="statusDot online" title={itx("etsSyncAndExport.online", "Online")}></span> &nbsp;{" "}
        {state.translate("etsSyncAndExport.online", "Online")}
        {connectionMode === "local" && (
          <span
            style={{ marginLeft: "6px", opacity: 0.9, fontSize: "0.82em", color: "var(--knxColorGreen, #4caf50)" }}
            title={itx(
              "etsDialog.localModeTooltip",
              "Connected directly to ETS6 on this machine — no cloud relay or PIN required",
            )}
          >
            {itx("etsDialog.localMode", "(Local ETS6)")}
          </span>
        )}
        {etsVersion && (
          <span style={{ marginLeft: "6px", opacity: 0.75, fontSize: "0.85em" }}>{`(v${etsVersion})`}</span>
        )}
        {etsProjectName && (
          <span
            style={{ marginLeft: "6px", opacity: 0.75, fontSize: "0.85em" }}
            title={itx("etsDialog.connectedProjectTooltip", "Connected ETS project")}
          >
            {`- ${etsProjectName}`}
          </span>
        )}
        {isUiModeProductAdmin() && etsBuildDate && (
          <span style={{ marginLeft: "6px", opacity: 0.6, fontSize: "0.8em" }}>{`[${etsBuildDate}]`}</span>
        )}
      </span>
    ) : (
      <span className="statusContainer offline" style={generalStyle}>
        <span className="statusDot offline" title={itx("etsSyncAndExport.offline", "Offline")}></span> &nbsp;{" "}
        {state.translate("etsSyncAndExport.offline", "Offline")}
      </span>
    );
  }

  function MoreETSButton(props: {}) {
    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        {showMoreButtons && (
          <button
            className="button"
            style={{ width: "100%" }}
            onClick={async () => {
              if (!state.projectLicense.checkLicenses(ProjectLicenseType.EtsExport)) {
                state.dialogManager.openDialog(<ToShopLicensesDialog />);
                return;
              }
              const shouldContinue = await askForGroupAddressUpdate(true);
              if (!shouldContinue) return;
              await onClickExport();
            }}
            // iconName="file-download"
            disabled={false}
          >
            {/* {state.translate("menuBar.export", "Export")} */}
            <i className={`las la-file-download`} style={{ paddingRight: "5px" }}></i>
            {state.translate("etsSyncAndExport.exportData", "ETS Austauschdatei")}
          </button>
        )}
        {getLastReadbackSummary() && showMoreButtons && (
          <div style={{ paddingBottom: "2px" }}>
            <button
              className="button"
              style={{ width: "100%" }}
              disabled={!getLastReadbackSummary()}
              onClick={() => {
                const summary = getLastReadbackSummary();
                if (summary) {
                  state.dialogManager.openDialog(<EtsReadbackResultDialog summary={summary} />, true);
                }
              }}
            >
              <i className="las la-history" style={{ marginRight: "8px" }} />
              {itx("etsSyncAndExport.viewLastReadback", "View last readback summary")}
            </button>
          </div>
        )}

        {browserName != "safari" && showMoreButtons && (
          <button
            className="button"
            style={{ width: "100%" }}
            onClick={openEtsLogViewer}
            // iconName="history"
          >
            <i className={`las la-history`} style={{ paddingRight: "5px" }}></i>
            {state.translate("menuBar.viewETSLog", "View ETS Log")}
          </button>
        )}
        <button
          className="button dialogHeaderButton"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "30px",
            alignSelf: "center",
            borderRadius: "15px",
            // boxShadow: "none",
            height: "auto",
          }}
          onClick={() => {
            setShowMoreButtons(!showMoreButtons);
          }}
          disabled={false}
        >
          {!showMoreButtons && (
            <i
              className="las la-angle-down"
              // data-tooltip-id="tv-tooltip"
              // data-tooltip-content={"Show more"}
              // data-tooltip-place="bottom"
            />
          )}
          {showMoreButtons && (
            <i
              className="las la-angle-up"
              // data-tooltip-id="tv-tooltip"
              // data-tooltip-content={"Show less"}
              // data-tooltip-place="bottom"
            />
          )}
        </button>
      </div>
    );
  }

  function formatDeveloperTimingMs(value: any): string {
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) return "";
    return numberValue >= 1000 ? `${(numberValue / 1000).toFixed(1)}s` : `${Math.round(numberValue)}ms`;
  }

  function formatDeveloperTimingNumber(value: any): string {
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) return "";
    return `${numberValue}`;
  }

  function DeveloperPushTimingPanel() {
    if (!isUiModeDeveloper() || !lastPushDeveloperTiming) {
      return null;
    }

    const timing = lastPushDeveloperTiming;
    const importIssues = Array.isArray(timing.importIssues) ? timing.importIssues : [];
    const primaryImportIssueCount = importIssues.length
      ? importIssues.filter((issue: any) => String(issue?.severity || "").toLowerCase() !== "secondary").length
      : Number(timing.importIssueCount || 0);
    const secondaryWarningCount = importIssues.length
      ? importIssues.filter((issue: any) => String(issue?.severity || "").toLowerCase() === "secondary").length
      : Number(timing.secondaryWarningCount || 0);
    const pushErrorLabel = "Push errors";
    const warningLabel = "Warnings"; // no itx
    const primaryRows = [
      ["Total", formatDeveloperTimingMs(timing.totalPluginImportMs)], // no itx
      ["Topology", formatDeveloperTimingMs(timing.topologyMs)], // no itx
      ["Devices", formatDeveloperTimingMs(timing.topologyImportDeviceMs)], // no itx
      ["Create/Add", formatDeveloperTimingMs(timing.topologyCreateOrReplaceMs)], // no itx
      ["Params", formatDeveloperTimingMs(timing.topologyImportParamsMs)], // no itx
      ["Modules", formatDeveloperTimingMs(timing.topologyModuleParamsMs)], // no itx
      ["Signals", formatDeveloperTimingMs(timing.topologyImportSignalsMs)], // no itx
      ["Building", formatDeveloperTimingMs(timing.buildingStructureMs)], // no itx
    ].filter((row) => row[1]);
    const detailRows = [
      ["Device add", formatDeveloperTimingMs(timing.topologyDeviceAddMs)], // no itx
      ["Device delete", formatDeveloperTimingMs(timing.topologyDeviceDeleteMs)], // no itx
      ["Devices", formatDeveloperTimingNumber(timing.topologyDeviceCount)], // no itx
      ["Created", formatDeveloperTimingNumber(timing.topologyDeviceCreateCount)], // no itx
      ["Replaced", formatDeveloperTimingNumber(timing.topologyDeviceReplaceCount)], // no itx
      ["Reused", formatDeveloperTimingNumber(timing.topologyExistingDeviceReuseCount)], // no itx
      ["Compat reuse", formatDeveloperTimingNumber(timing.topologyExistingDeviceCatalogIdCompatibleReuseCount)], // no itx
      ["Param enum", formatDeveloperTimingMs(timing.topologyImportParamsEnumerateRefsMs)], // no itx
      ["Param set", formatDeveloperTimingMs(timing.topologyImportParamsSetValuesMs)], // no itx
      ["Param refs", formatDeveloperTimingNumber(timing.topologyImportParamsActiveRefCount)], // no itx
      ["Param text skip", formatDeveloperTimingNumber(timing.topologyImportParamsTextSkippedCount)], // no itx
      ["Param text set", formatDeveloperTimingNumber(timing.topologyImportParamsTextSetCount)], // no itx
      ["Param changed", formatDeveloperTimingNumber(timing.topologyImportParamsValueChangedCount)], // no itx
      [pushErrorLabel, formatDeveloperTimingNumber(primaryImportIssueCount)], // no itx
      [warningLabel, formatDeveloperTimingNumber(secondaryWarningCount)], // no itx
      ["Cleanup protected", formatDeveloperTimingNumber(timing.topologyCleanupProtectedDeviceCount)], // no itx
      ["Module passes", formatDeveloperTimingNumber(timing.topologyModuleParamPasses)], // no itx
      ["Modules retried", formatDeveloperTimingNumber(timing.topologyModuleParamModulesRetried)], // no itx
    ].filter((row) => row[1]);

    return (
      <div
        style={{
          marginTop: "8px",
          padding: "8px 10px",
          border: "1px solid rgba(80, 130, 200, 0.35)",
          borderRadius: "6px",
          background: "rgba(80, 130, 200, 0.06)",
          fontSize: "0.86em",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px", fontWeight: 700 }}>
          <i className="las la-stopwatch" />
          <span>{noItx("Last push timing")}</span>
          {timing.topologySlowestDevice && (
            <span style={{ marginLeft: "auto", fontWeight: 400, opacity: 0.78 }}>
              {noItx("Slowest:")} {timing.topologySlowestDevice} (
              {formatDeveloperTimingMs(timing.topologySlowestDeviceMs)})
            </span>
          )}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(94px, 1fr))",
            gap: "5px",
            marginBottom: "6px",
          }}
        >
          {primaryRows.map(([label, value]) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
              <span style={{ opacity: 0.7 }}>{label}</span>
              <span style={{ fontWeight: 700 }}>{value}</span>
            </div>
          ))}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(116px, 1fr))",
            gap: "4px 10px",
            opacity: 0.82,
          }}
        >
          {detailRows.map(([label, value]) => {
            const canOpenWarnings =
              importIssues.length > 0 &&
              ((label === pushErrorLabel && primaryImportIssueCount > 0) ||
                (label === warningLabel && secondaryWarningCount > 0));
            return (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
                {canOpenWarnings ? (
                  <button
                    type="button"
                    style={{
                      padding: 0,
                      border: "none",
                      background: "transparent",
                      color: "var(--knxColorBlue)",
                      font: "inherit",
                      lineHeight: "inherit",
                      textAlign: "left",
                      textDecoration: "underline",
                      textUnderlineOffset: "2px",
                      cursor: "pointer",
                    }}
                    onClick={() => showETSPushImportIssues(importIssues)}
                  >
                    {label}
                  </button>
                ) : (
                  <span>{label}</span>
                )}
                <span>{value}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function ExplainationBox(props: { text: string }) {
    const rows = new Array<any>();
    const text = props.text;
    const textRows = text.split(/\\n|\n/);
    textRows.forEach((r) => {
      let elements = new Array<any>();

      const splitFirstTag = r.split(/(<[^>]+>)/g);
      splitFirstTag.forEach((part) => {
        let part1 = part;
        if (part1.startsWith("<") && part1.endsWith(">")) {
          const tag = part1.replace(">", "").replace("<", "");
          elements.push(<span style={{ fontWeight: "bold" }}>{tag}</span>);
        } else {
          elements.push(<span>{part1}</span>);
        }
      });

      rows.push(<div style={{ whiteSpace: "initial" }}>{elements}</div>);
    });

    return (
      <div
        style={{
          border: "1px solid rgba(255, 165, 0, 0.7)",
          borderRadius: "6px",
          padding: "10px 14px",
          marginBottom: "10px",
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          gap: "12px",
          background: "rgba(255, 165, 0, 0.06)",
        }}
      >
        <i
          className="las la-exclamation-circle"
          style={{ fontSize: "20px", color: "orange", flexShrink: 0, marginTop: "1px" }}
        />
        <div style={{ flex: 1, whiteSpace: "initial", fontSize: "0.9em", lineHeight: "1.5" }}>{rows}</div>
      </div>
    );
  }

  let browserName = "";
  const browser = detect();
  if (browser) {
    browserName = browser.name;
  }

  async function resetBusyToChecking() {
    await resetEtsOperationState();
    setIsSyncInProgress(false);
    setIsReadbackInProgress(false);
    setReadbackSource(undefined);
    scheduleEtsStatusCheck();
  }

  function buttonDisabled(licenseType: ProjectLicenseType): boolean {
    const commandSocketReady = Boolean(getActiveEtsSocket()?.isConnected);
    const localProjectRequired = connectionMode === "local" && localEtsProjects.length > 1 && !selectedLocalProjectId;
    if (state.projectLicense.checkLicenses(licenseType)) {
      return !initialStatusChecked || !state.etsIsOnline || !commandSocketReady || localProjectRequired;
    }
    return !initialStatusChecked || !state.etsIsOnline || !commandSocketReady || localProjectRequired;
  }

  function getNormalizedOperationState(data: EtsOnlineOperationEvent | undefined): string {
    return String(data?.state || data?.eventKind || "")
      .trim()
      .toLowerCase();
  }

  function isTerminalOnlineOperation(data: EtsOnlineOperationEvent | undefined): boolean {
    const normalizedState = getNormalizedOperationState(data);
    // "restarting" is a synthetic non-terminal state we emit when the plugin detects a
    // premature Finished event during a mid-download device restart — never treat it as done.
    if (normalizedState === "restarting") return false;
    return (
      Boolean(data?.isTerminal) ||
      ["finished", "failed", "canceled", "cancelled", "stopped", "aborted", "error"].includes(normalizedState)
    );
  }

  function isSuccessfulOnlineOperation(data: EtsOnlineOperationEvent | undefined): boolean {
    return getNormalizedOperationState(data) === "finished";
  }

  function getTerminalOnlineOperationMessage(data: EtsOnlineOperationEvent): string {
    const normalizedState = getNormalizedOperationState(data);
    if (normalizedState === "finished") {
      return itx("etsDialog.programDeviceComplete", "ETS device download complete.");
    }
    if (normalizedState === "canceled" || normalizedState === "cancelled") {
      return (
        data.errorMessage || data.message || itx("etsDialog.programDeviceCanceled", "ETS device download was canceled.")
      );
    }
    if (normalizedState === "stopped" || normalizedState === "aborted") {
      return (
        data.errorMessage || data.message || itx("etsDialog.programDeviceStopped", "ETS device download was stopped.")
      );
    }
    return data.errorMessage || data.message || itx("etsDialog.programDeviceFailed", "ETS device download failed.");
  }

  function applyOnlineOperationFeedback(
    data: EtsOnlineOperationEvent,
    _source:
      | "event" // no itx
      | "poll", // no itx
  ) {
    if (!data) return;

    const eventAddress = String(data.physicalAddress || "");
    const selectedAddress = deviceProgramAddress.trim();
    if (eventAddress && selectedAddress && eventAddress !== selectedAddress) {
      return;
    }

    if (data.busConnectionInfo) {
      setBusInfo((current) => ({
        ...(current || {}),
        lastBusConnectionInfo: data.busConnectionInfo,
      }));
    }

    setDeviceProgramProgress(data);

    const terminal = isTerminalOnlineOperation(data);
    if (terminal) {
      setIsProgrammingDevice(false);
      if (isSuccessfulOnlineOperation(data)) {
        // Silently clear — no "ETS download complete" message
        setDeviceProgramProgress(null);
        setDeviceProgramResult(null);
      } else {
        setDeviceProgramResult({
          status: "ERROR",
          message: getTerminalOnlineOperationMessage(data),
        });
      }
      loadProjectDevicesForProgramming();
      return;
    }

    if (
      data.requiresProgrammingMode ||
      data.state === "Running" ||
      data.state === "Restarting" ||
      data.state === "Waiting" ||
      typeof data.progress === "number"
    ) {
      setIsProgrammingDevice(true);
    }
  }

  /** Renders an inline warning banner when the connected ETS plugin is below a required version. */
  function VersionWarning({ requiredVersion }: { requiredVersion: string }) {
    if (!initialStatusChecked || !state.etsIsOnline || !etsVersion || isVersionSufficient(etsVersion, requiredVersion))
      return null;
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          background: "rgba(255,165,0,0.15)",
          border: "1px solid orange",
          borderRadius: "4px",
          padding: "4px 10px",
          marginTop: "4px",
          marginBottom: "4px",
          fontSize: "0.82em",
          color: "orange",
        }}
      >
        <i className="las la-exclamation-triangle" style={{ flexShrink: 0 }} />
        {itx("etsDialog.versionTooLow", `Requires ETS plugin v${requiredVersion}+. Connected: v${etsVersion || "?"}`)
          .replace("$req", requiredVersion)
          .replace(
            "$cur", // no itx
            etsVersion || "?",
          )}
      </div>
    );
  }

  function normalizeProjectDevices(rawDevices: any[]): EtsProjectDevice[] {
    return rawDevices
      .map((device) => ({
        etsId: String(device?.etsId || ""),
        name: String(device?.name || ""),
        description: String(device?.description || ""),
        physicalAddress: String(device?.physicalAddress || ""),
        lineName: String(device?.lineName || ""),
        lineAddress: String(device?.lineAddress || ""),
        roomName: String(device?.roomName || ""),
        productName: String(device?.productName || ""),
        orderNumber: String(device?.orderNumber || ""),
        manufacturerName: String(device?.manufacturerName || ""),
        serialNumber: String(device?.serialNumber || ""),
        downloadCounter: Number(device?.downloadCounter || 0),
        lastDownload: String(device?.lastDownload || ""),
        applicationProgramLoaded: Boolean(device?.applicationProgramLoaded),
        individualAddressLoaded: Boolean(device?.individualAddressLoaded),
        parametersLoaded: Boolean(device?.parametersLoaded),
        communicationPartLoaded: Boolean(device?.communicationPartLoaded),
        mediumConfigLoaded: Boolean(device?.mediumConfigLoaded),
        connectionAvailable: Boolean(device?.connectionAvailable),
      }))
      .filter((device) => device.physicalAddress)
      .sort((a, b) => comparePhysicalAddresses(a.physicalAddress, b.physicalAddress));
  }

  function comparePhysicalAddresses(a: string, b: string): number {
    const pa = a.split(".").map((part) => Number(part));
    const pb = b.split(".").map((part) => Number(part));
    for (let i = 0; i < 3; i++) {
      const diff = (pa[i] || 0) - (pb[i] || 0);
      if (diff !== 0) return diff;
    }
    return a.localeCompare(b);
  }

  function getFallbackProjectDevices(): EtsProjectDevice[] {
    return state
      .allDevices()
      .filter((device) => device.template && device.fullPhysicalAddress && device.fullPhysicalAddress !== "0.0.0")
      .map((device) => ({
        etsId: device.etsId || "",
        name: `${device.fullTag || ""} ${device.name || ""}`.trim(),
        physicalAddress: device.fullPhysicalAddress,
        productName: device.fullname || "",
      }))
      .sort((a, b) => comparePhysicalAddresses(a.physicalAddress, b.physicalAddress));
  }

  function getProjectDeviceLabel(device: EtsProjectDevice): string {
    const name = device.name ? ` - ${device.name}` : "";
    const product = device.productName ? ` (${device.productName})` : "";
    const serial = device.serialNumber ? ` SN:${device.serialNumber}` : "";
    return `${device.physicalAddress}${name}${product}${serial}`;
  }

  function validatePhysicalAddress(address: string): string | undefined {
    const trimmed = address.trim();
    const parts = trimmed.split(".");
    if (parts.length !== 3 || parts.some((part) => !/^\d+$/.test(part))) {
      return itx("etsDialog.invalidPhysicalAddressSyntax", "Invalid physical address. Use x.y.z numeric syntax.");
    }

    const [area, line, device] = parts.map((part) => Number(part));
    if (area < 0 || area > 15 || line < 0 || line > 15 || device < 0 || device > 255) {
      return itx("etsDialog.invalidPhysicalAddressRange", "Invalid physical address. Range is 0.0.0 to 15.15.255.");
    }

    return undefined;
  }

  function getDeviceDownloadPreferences(): Record<string, EtsDeviceDownloadType> {
    try {
      return JSON.parse(localStorage.getItem(DEVICE_DOWNLOAD_PREFERENCES_KEY) || "{}");
    } catch {
      return {};
    }
  }

  function getSavedDownloadType(address: string): EtsDeviceDownloadType | undefined {
    return getDeviceDownloadPreferences()[address.trim()];
  }

  function saveDownloadType(address: string, downloadType: EtsDeviceDownloadType) {
    if (!address.trim()) return;
    const preferences = getDeviceDownloadPreferences();
    preferences[address.trim()] = downloadType;
    localStorage.setItem(DEVICE_DOWNLOAD_PREFERENCES_KEY, JSON.stringify(preferences));
  }

  function applyDeviceProgramAddress(address: string) {
    const nextAddress = address.trim();
    setDeviceProgramAddress(nextAddress);
    setDeviceProgramResult(null);
    setDeviceProgramProgress(null);
    setDeviceStatus(null);
    const savedDownloadType = getSavedDownloadType(nextAddress);
    if (savedDownloadType) {
      setDeviceProgramDownloadType(savedDownloadType);
    }
    if (!validatePhysicalAddress(nextAddress)) {
      checkSelectedDeviceStatus(nextAddress, false);
    }
  }

  function getDownloadTypeHint(downloadType: EtsDeviceDownloadType): string {
    switch (downloadType) {
      case "All":
        return itx(
          "etsDialog.downloadHintAll",
          "Downloads address, application, parameters and communication data like ETS Download All.",
        );
      case "Partial":
        return itx("etsDialog.downloadHintPartial", "Downloads only ETS parts that are marked as changed.");
      case "LoadApplicationProgram":
        return itx(
          "etsDialog.downloadHintApplication",
          "Downloads the application program and application data to an already addressed device.",
        );
      case "LoadNetworkConfiguration":
        return itx(
          "etsDialog.downloadHintNetwork",
          "Downloads individual address and network configuration. ETS may ask for the programming button.",
        );
      case "LoadNetworkConfigurationBySerialNumber":
        return itx(
          "etsDialog.downloadHintNetworkSerial",
          "Downloads network configuration by KNX serial number. The ETS device must have a serial number.",
        );
      case "OverwriteIndividualAddress":
        return itx(
          "etsDialog.downloadHintOverwrite",
          "Writes the project address by overwriting a known current device address, without the programming button.",
        );
      default:
        return "";
    }
  }

  async function loadProjectDevicesForProgramming() {
    setIsLoadingProjectDevices(true);
    try {
      const res = await getActiveEtsSocket()?.sendCommandAsync("listProjectDevices", {}, 30000);
      if (res?.status === "OK") {
        const devices = normalizeProjectDevices(res.data || []);
        setProjectDevices(devices);
        if (!deviceProgramAddress && devices[0]?.physicalAddress) {
          applyDeviceProgramAddress(devices[0].physicalAddress);
        }
      } else if (res?.message) {
        showToast({ isError: true, text: res.message });
      }
    } catch (ex) {
      logger.error("Error loading ETS project devices:", ex);
    } finally {
      setIsLoadingProjectDevices(false);
    }
  }

  async function loadBusInfo() {
    setIsLoadingBusInfo(true);
    try {
      const res = await getActiveEtsSocket()?.sendCommandAsync("listBusInterfaces", {}, 30000);
      if (res?.status === "OK") {
        setBusInfo({
          supported: Boolean(res.supported),
          fallbackConnection: String(res.fallbackConnection || ""),
          lastBusConnectionInfo: String(res.lastBusConnectionInfo || ""),
          message: String(res.message || ""),
        });
      }
    } catch (ex) {
      logger.error("Error loading ETS bus interface info:", ex);
    } finally {
      setIsLoadingBusInfo(false);
    }
  }

  async function checkSelectedDeviceStatus(address = deviceProgramAddress, probeBus = false) {
    const addr = address.trim();
    if (!addr || validatePhysicalAddress(addr)) return;
    setIsCheckingDeviceStatus(true);
    try {
      const res = await getActiveEtsSocket()?.sendCommandAsync(
        "checkDeviceStatus", // no itx
        { physicalAddress: addr, probeBus },
        probeBus ? 45000 : 15000,
      );
      if (res?.status === "OK") {
        setDeviceStatus(res);
        if (res.busConnectionInfo || res.lastBusConnectionInfo) {
          setBusInfo((current) => ({
            ...(current || {}),
            lastBusConnectionInfo: String(res.busConnectionInfo || res.lastBusConnectionInfo || ""),
          }));
        }
      } else if (res?.message) {
        setDeviceStatus({
          status: "ERROR",
          physicalAddress: addr,
          message: res.message,
        });
      }
    } catch (ex) {
      logger.error("Error checking ETS device status:", ex);
    } finally {
      setIsCheckingDeviceStatus(false);
    }
  }

  async function handleSetProgrammingMode(enabled: boolean) {
    const addr = deviceProgramAddress.trim();
    const invalidAddress = validatePhysicalAddress(addr);
    if (invalidAddress) {
      setDeviceProgramResult({ status: "ERROR", message: invalidAddress });
      return;
    }

    const confirmed = window.confirm(
      enabled
        ? itx(
            "etsDialog.confirmEnableProgrammingMode",
            `Enable programming mode on ${addr}? Only do this when you are sure this is the intended KNX device.`,
          )
        : itx("etsDialog.confirmDisableProgrammingMode", `Disable programming mode on ${addr}?`),
    );
    if (!confirmed) {
      setDeviceProgramResult({
        status: "ERROR",
        message: itx("etsDialog.programmingModeCanceled", "Programming mode request canceled."),
      });
      return;
    }

    setIsSettingProgrammingMode(true);
    setDeviceProgramResult(null);
    try {
      const res = await getActiveEtsSocket()?.sendCommandAsync(
        "setDeviceProgrammingMode", // no itx
        { physicalAddress: addr, enabled },
        45000,
      );
      setDeviceProgramResult({
        status: res?.status === "OK" ? "OK" : "ERROR",
        message:
          res?.message ||
          itx(
            "etsDialog.programmingModeRequestFailed",
            "Programming mode request failed. Check ETS bus access rights.",
          ),
      });
    } catch (ex) {
      setDeviceProgramResult({
        status: "ERROR",
        message: itx(
          "etsDialog.programmingModeRequestFailed",
          "Programming mode request failed. Check ETS bus access rights.",
        ),
      });
    } finally {
      setIsSettingProgrammingMode(false);
    }
  }

  async function handleProgramDevice() {
    const addr = deviceProgramAddress.trim();
    const invalidAddress = validatePhysicalAddress(addr);
    if (invalidAddress) {
      setDeviceProgramResult({ status: "ERROR", message: invalidAddress });
      return;
    }

    if (isSyncInProgress || isReadbackInProgress) {
      setDeviceProgramResult({
        status: "INFO", // no itx
        message: itx(
          "etsDialog.pluginOperationAlreadyRunning",
          "An ETS operation is already running. Wait for it to complete before starting a device download.",
        ),
      });
      return;
    }

    if (isProgrammingDevice) {
      setDeviceProgramResult({
        status: "INFO", // no itx
        message: itx(
          "etsDialog.programDeviceAlreadyRunning",
          `A download operation is already running for ${addr}. Wait for it to complete before starting another.`,
        ),
      });
      return;
    }

    const payload: any = { physicalAddress: addr, downloadType: deviceProgramDownloadType };
    if (deviceProgramDownloadType === "LoadNetworkConfigurationBySerialNumber") {
      payload.useSerialNumber = true;
    }
    if (deviceProgramDownloadType === "OverwriteIndividualAddress") {
      const invalidCurrentAddress = validatePhysicalAddress(deviceProgramCurrentAddress);
      if (invalidCurrentAddress) {
        setDeviceProgramResult({
          status: "ERROR",
          message: itx("etsDialog.invalidCurrentPhysicalAddress", "Invalid current device address for overwrite."),
        });
        return;
      }
      payload.currentAddress = deviceProgramCurrentAddress.trim();
    }

    setIsProgrammingDevice(true);
    setDeviceProgramResult(null);
    setDeviceProgramProgress({
      state: "Queued", // no itx
      physicalAddress: addr,
      message: itx("etsDialog.programDeviceAddedToQueue", "Download added to queue."),
    });
    try {
      const res = await getActiveEtsSocket()?.sendCommandAsync("programDevice", payload, 60000);
      if (res?.status === "OK") {
        if (deviceProgramDownloadType === "LoadNetworkConfigurationBySerialNumber") {
          saveDownloadType(addr, deviceProgramDownloadType);
        }
        if (res.requiresProgrammingModeExpected) {
          setDeviceProgramProgress({
            state: "WaitingForProgrammingMode", // no itx
            progress: 0,
            physicalAddress: res.physicalAddress || addr,
            deviceName: res.deviceName || "",
            message: itx(
              "etsDialog.programmingModeRequiredFrontend",
              "ETS is waiting for programming mode. Set programming mode from this app, or press the physical programming button if remote mode is not possible.",
            ),
            requiresProgrammingMode: true,
          });
        }
        setDeviceProgramResult({
          status: "OK", // no itx
          message: itx(
            "etsDialog.programDeviceSuccess",
            `Download queued for ${res.deviceName || addr} (${res.physicalAddress || addr})`,
          ),
        });
      } else {
        const message =
          res?.message || itx("etsDialog.programDeviceError", "Device programming failed or device not found.");
        setDeviceProgramResult({
          status: "ERROR",
          message,
        });
        setDeviceProgramProgress({
          state: "Failed", // no itx
          physicalAddress: addr,
          message,
          errorMessage: message,
          isTerminal: true,
        });
        setIsProgrammingDevice(false);
      }
    } catch (ex) {
      const message = itx("etsDialog.programDeviceError", "Device programming request failed.");
      setDeviceProgramResult({
        status: "ERROR",
        message,
      });
      setDeviceProgramProgress({
        state: "Failed", // no itx
        physicalAddress: addr,
        message,
        errorMessage: message,
        isTerminal: true,
      });
      setIsProgrammingDevice(false);
    }
  }

  const invalidDeviceProgramAddress = deviceProgramAddress ? validatePhysicalAddress(deviceProgramAddress) : undefined;
  const invalidCurrentProgramAddress =
    deviceProgramDownloadType === "OverwriteIndividualAddress" && deviceProgramCurrentAddress
      ? validatePhysicalAddress(deviceProgramCurrentAddress)
      : undefined;
  const selectedProjectDevice = (projectDevices.length > 0 ? projectDevices : getFallbackProjectDevices()).find(
    (device) => device.physicalAddress === deviceProgramAddress.trim(),
  );
  const isReadbackFromEtsInProgress = isReadbackInProgress && readbackSource !== "json";
  const isReadbackFromJsonInProgress = isReadbackInProgress && readbackSource === "json";
  const readbackBetaBadge = isUiModeProductAdmin() ? (
    <sup style={{ marginLeft: "4px", fontSize: "0.65em", letterSpacing: "0" }}>BETA</sup> // no itx
  ) : null;
  const isEtsPluginOperationInProgress = isSyncInProgress || isReadbackFromEtsInProgress || isProgrammingDevice;
  const programButtonDisabled =
    !state.etsIsOnline ||
    isSyncInProgress ||
    isReadbackInProgress ||
    isProgrammingDevice ||
    !deviceProgramAddress.trim() ||
    Boolean(invalidDeviceProgramAddress) ||
    Boolean(invalidCurrentProgramAddress) ||
    (deviceProgramDownloadType === "OverwriteIndividualAddress" && !deviceProgramCurrentAddress.trim()) ||
    !isVersionSufficient(etsVersion, ETS_MIN_VERSION.PROGRAM_DEVICE);
  const programmingModeButtonDisabled =
    !state.etsIsOnline ||
    isSettingProgrammingMode ||
    isSyncInProgress ||
    isReadbackInProgress ||
    !deviceProgramAddress.trim() ||
    Boolean(invalidDeviceProgramAddress) ||
    !isVersionSufficient(etsVersion, ETS_MIN_VERSION.PROGRAM_DEVICE);

  return (
    <BaseDialog
      title={state.translate("etsSyncAndExport.etsApp", "ETS Application")}
      noOkButton={true}
      abortCaption={state.translate("close", "Schließen")}
      onClose={() => {
        return true;
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          paddingLeft: "20px",
          paddingRight: "20px",
        }}
      >
        {/*
        <div style={{ marginLeft: "10px", textAlign: "center" }}>
          <b>{state.translate("etsSyncAndExport.adressesNew", "Sind die Gruppenadressen aktuell?")}</b>
        </div>
        <button
          className="button"
          onClick={() => generateGroupAddresses()}
          // iconName="list-ol"
        >
          {state.translate("etsSyncAndExport.genAdress", "Gruppenadressen generieren")}
        </button>

        <div style={{ borderBottom: "1px solid black", height: "10px", marginBottom: "15px" }}></div> */}
        {/* <br /> */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            marginBottom: "10px",
            padding: "10px 14px",
            border: "1px solid var(--border-color, rgba(128,128,128,0.25))",
            borderRadius: "6px",
            background: "var(--bg-secondary, rgba(128,128,128,0.04))",
          }}
        >
          {/* Row 1 – Connection status */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
            <span style={{ fontWeight: "bold", flexShrink: 0 }}>
              {itx("etsSyncAndExport.connectionStatus", "ETS Application")}:
            </span>
            <span
              data-tooltip-id="tv-tooltip"
              data-tooltip-place="bottom"
              data-tooltip-content={buttonDisabled(ProjectLicenseType.EtsExport) ? dataTooltipContent : ""}
              style={{ minWidth: 0 }}
            >
              <EtsApplicationConnectionSpan />
            </span>
            {(isSyncInProgress || isReadbackInProgress) && (
              <button
                className="button"
                style={{
                  padding: "0 6px",
                  width: "28px",
                  height: "28px",
                  minWidth: "unset",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
                title={itx("etsDialog.resetBusyStatus", "Reset status and check ETS connection again")}
                onClick={resetBusyToChecking}
              >
                <i className="las la-redo-alt" style={{ fontSize: "14px" }} />
              </button>
            )}
          </div>

          {/* Row 2 – Project selector + PIN */}
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: "6px 12px",
            }}
          >
            {connectionMode === "local" && localEtsProjects.length > 1 && (
              <label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <span style={{ whiteSpace: "nowrap", opacity: 0.8, fontSize: "0.9em" }}>
                  {itx("etsDialog.localProjectLabel", "ETS Project")}
                </span>
                <select
                  className="select"
                  value={selectedLocalProjectId}
                  onChange={(ev) => selectLocalProject(ev.target.value)}
                  disabled={isSyncInProgress || isReadbackInProgress}
                  style={{ minWidth: "200px", maxWidth: "280px", height: "28px" }}
                  title={itx("etsDialog.localProjectSelector", "Select local ETS project")}
                >
                  {localEtsProjects.map((project) => (
                    <option key={project.projectId || project.projectGuid} value={project.projectId}>
                      {project.projectName || project.projectId || project.projectGuid}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "6px" }}>
              <span style={{ whiteSpace: "nowrap", opacity: 0.8, fontSize: "0.9em" }}>
                {itx("etsSyncAndExport.etsProjectPin", "ETS Projekt Pin")}
              </span>
              <input
                type="text"
                ref={pinRef}
                onChange={(ev) => applyPin(ev.target.value)}
                style={{
                  width: "70px",
                  textAlign: "center",
                  textTransform: "uppercase",
                  ...(pinInputValue && initialStatusChecked
                    ? connectionMode === "relay" && state.etsIsOnline
                      ? { background: "rgba(76,175,80,0.15)" } // no itx
                      : {} //{ background: "rgba(220,40,40,0.12)" }
                    : {}),
                }}
                placeholder="PIN" // no itx
                maxLength={6} // no itx
                value={pinInputValue}
              />
              {lastUsedPin && (
                <button
                  className="button"
                  style={{
                    padding: "0 6px",
                    width: "28px",
                    height: "28px",
                    minWidth: "unset",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                  title={`${itx("etsDialog.pasteLastPin", "Paste last PIN")}: ${lastUsedPin}`}
                  onClick={() => applyPin(lastUsedPin)}
                >
                  <i className="las la-history" style={{ fontSize: "14px" }} />
                </button>
              )}
              {pinInputValue && (
                <button
                  className="button"
                  style={{
                    padding: "0 6px",
                    width: "28px",
                    height: "28px",
                    minWidth: "unset",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                  title={itx("etsDialog.clearPin", "Clear PIN")}
                  onClick={() => {
                    state.projectMetadata.etsPin = "";
                    setPinInputValue("");
                    beginEtsStatusCheck();
                    setEtsProjectName("");
                    closeEtsWebSocket();
                    relayPinPreferredUntilRef.current = 0;
                    state.etsIsOnline = false;
                    state.projectMetadata.raiseOnChange("etsPin");
                    scheduleEtsStatusCheck();
                  }}
                >
                  <i className="las la-trash" style={{ fontSize: "14px" }} />
                </button>
              )}
              {connectionMode === "local" && pinInputValue != "" && (
                <span
                  style={{ fontSize: "0.82em", opacity: 0.65, whiteSpace: "nowrap" }}
                  title={itx(
                    "etsDialog.localPinHintTooltip",
                    "The frontend is connected directly to the local ETS6 plugin. The relay PIN is not used in this mode.",
                  )}
                >
                  {itx("etsDialog.localPinHint", "PIN not in use")}
                </span>
              )}
            </div>
          </div>
        </div>
        <ExplainationBox
          text={itx(
            "etsDialog.physicalConnectionExplanation",
            "The following will not be overwritten in ETS:\n- <Physical addresses 0, 1, 200 to 255>\n- <Group addresses 31/5/x>\nAll other data will be overwritten.",
          )}
        />

        {/* <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center" }}>
          <input
            type="checkbox"
            onClick={(ev: any) => {
              setOnlyOne(ev.target.checked);
            }}
            style={{ height: "20px" }}
          ></input>
          {state.translate("etsSyncAndExport.onlyOneDevice", "Nur für einen Taster/Aktor")}
        </div> */}

        {onlyOne && <OnlyOneSelectElm />}
        {isUiModeDeveloper() && SHOW_ETS_PARAM_TEXT_IMPORT_MODE_SWITCH && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              padding: "8px 10px",
              border: "1px solid rgba(128,128,128,0.22)",
              borderRadius: "6px",
              background: "rgba(128,128,128,0.06)",
              fontSize: "0.9em",
            }}
          >
            <span>{itx("etsDialog.paramTextImportMode", "Push modes")}</span>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                userSelect: "none",
              }}
            >
              <span style={{ opacity: paramTextImportMode === "fast" ? 1 : 0.6 }}>
                {itx("etsDialog.paramTextMode1", "Fast")}
              </span>
              <button
                type="button" // no itx
                role="switch" // no itx
                aria-checked={paramTextImportMode === "complete"}
                aria-label={itx("etsDialog.paramTextImportMode", "Push modes")}
                disabled={isSyncInProgress || isReadbackInProgress}
                onClick={() => updateParamTextImportMode(paramTextImportMode === "complete" ? "fast" : "complete")}
                title={itx("etsDialog.completeParamTextPush", "Complete parameter text push")}
                style={{
                  position: "relative",
                  width: "46px",
                  height: "24px",
                  padding: 0,
                  border: "1px solid rgba(128,128,128,0.36)",
                  borderRadius: "999px",
                  background: paramTextImportMode === "complete" ? "var(--knxColorBlue)" : "rgba(128,128,128,0.24)",
                  cursor: isSyncInProgress || isReadbackInProgress ? "default" : "pointer",
                  opacity: isSyncInProgress || isReadbackInProgress ? 0.55 : 1,
                  transition: "background 120ms ease, opacity 120ms ease",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    top: "2px",
                    left: paramTextImportMode === "complete" ? "23px" : "2px",
                    width: "18px",
                    height: "18px",
                    borderRadius: "50%",
                    background: "white",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.28)",
                    transition: "left 120ms ease",
                  }}
                />
              </button>
              <span style={{ opacity: paramTextImportMode === "complete" ? 1 : 0.6 }}>
                {itx("etsDialog.paramTextMode2", "Complete")}
              </span>
            </div>
          </div>
        )}
        {/* isUiModeDeveloper() */}
        {/* {isUiModeProductAdmin() && (
          <button
            className="button"
            style={{ width: "100%" }}
            disabled={false}
            onClick={async () => {
              etsWebSocket?.sendEvent("systemreset", {}); // no itx
              showToast({
                text: itx("etsSyncAndExport.websocketResetSuccess", "WebSocket Reset success!"),
              });
            }}
          >
            <div>{state.translate("etsSyncAndExport.websocketReset", "WebSocket Reset")}</div>
          </button>
        )} */}

        <div style={{ height: "10px" }}></div>
        <div
          data-tooltip-id="tv-tooltip"
          data-tooltip-place="bottom"
          data-tooltip-content={buttonDisabled(ProjectLicenseType.EtsExport) ? dataTooltipContent : ""}
        >
          <button
            className="button"
            style={{ width: "100%" }}
            disabled={
              (connectionMode !== "local" && !hasUsableEtsPin) ||
              buttonDisabled(ProjectLicenseType.EtsExport) ||
              isSyncInProgress ||
              isReadbackInProgress ||
              isProgrammingDevice
            }
            onClick={async () => {
              const pin = ensureEtsPinApplied(effectiveEtsPin);
              if (isUsableEtsPin(pin) || connectionMode === "local") {
                rememberEtsPin(pin);
                const shouldContinue = await askForGroupAddressUpdate(true);
                if (!shouldContinue) return;
                await syncWithETS(getConfig(), pin);
              }
            }}
            data-tooltip-id="tv-tooltip"
            data-tooltip-place="bottom"
            data-tooltip-content={buttonDisabled(ProjectLicenseType.EtsExport) ? dataTooltipContent : ""}
          >
            {isSyncInProgress && (
              <div>
                <i className="las la-sync la-spin" />{" "}
                {state.translate("etsSyncAndExport.runningSync", "Daten werden zu ETS gepusht")}
              </div>
            )}
            {!isSyncInProgress && (
              <div>
                <i className="las la-file-export" />{" "}
                {onlyOne
                  ? itx("etsSyncAndExport.syncOneDevice", "Gerät zu ETS pushen")
                  : state.translate("etsSyncAndExport.sync", "Projekt zu ETS pushen")}
              </div>
            )}
          </button>
        </div>
        <DeveloperPushTimingPanel />
        {isUiModeProductAdmin() && (
          <div style={{ marginTop: "8px", marginBottom: "12px" }}>
            <div
              data-tooltip-id="tv-tooltip"
              data-tooltip-place="bottom"
              data-tooltip-content={buttonDisabled(ProjectLicenseType.EtsExport) ? dataTooltipContent : ""}
            >
              <button
                className="button"
                style={{ width: "100%" }}
                disabled={
                  (connectionMode !== "local" && !hasUsableEtsPin) ||
                  isSyncInProgress ||
                  isReadbackInProgress ||
                  isProgrammingDevice ||
                  buttonDisabled(ProjectLicenseType.EtsExport) ||
                  !isVersionSufficient(etsVersion, ETS_MIN_VERSION.READ_PROJECT_FROM_ETS)
                }
                onClick={async () => {
                  const pin = ensureEtsPinApplied(effectiveEtsPin);
                  if (isUsableEtsPin(pin) || connectionMode === "local") {
                    rememberEtsPin(pin);
                    state.dialogManager.closeDialog();
                    await readbackFromETS(getConfig(), false, pin, "parameters"); // no itx
                  }
                }}
                data-tooltip-id="tv-tooltip"
                data-tooltip-place="bottom"
                data-tooltip-content={buttonDisabled(ProjectLicenseType.EtsExport) ? dataTooltipContent : ""}
              >
                {isReadbackFromEtsInProgress && (
                  <div>
                    <i className="las la-sync la-spin" />{" "}
                    {state.translate(
                      "etsSyncAndExport.runningReadDeviceParametersFromETS",
                      "Reading device parameters from ETS",
                    )}
                  </div>
                )}
                {!isReadbackFromEtsInProgress && (
                  <div>
                    <i className="las la-sliders-h" />{" "}
                    {itx("etsSyncAndExport.readDeviceParametersFromETS", "Read ETS Parameters")}
                  </div>
                )}
              </button>
              <VersionWarning requiredVersion={ETS_MIN_VERSION.READ_PROJECT_FROM_ETS} />
            </div>

            {isUiModeProductAdmin() && (
              <div
                data-tooltip-id="tv-tooltip"
                data-tooltip-place="bottom"
                data-tooltip-content={buttonDisabled(ProjectLicenseType.EtsExport) ? dataTooltipContent : ""}
              >
                <button
                  className="button"
                  style={{ width: "100%" }}
                  disabled={
                    (connectionMode !== "local" && !hasUsableEtsPin) ||
                    isSyncInProgress ||
                    isReadbackInProgress ||
                    isProgrammingDevice ||
                    buttonDisabled(ProjectLicenseType.EtsExport) ||
                    !isVersionSufficient(etsVersion, ETS_MIN_VERSION.READ_PROJECT_FROM_ETS)
                  }
                  onClick={async () => {
                    const pin = ensureEtsPinApplied(effectiveEtsPin);
                    if (isUsableEtsPin(pin) || connectionMode === "local") {
                      rememberEtsPin(pin);
                      state.dialogManager.closeDialog();
                      await readbackFromETS(getConfig(), false, pin, "full"); // no itx
                    }
                  }}
                  data-tooltip-id="tv-tooltip"
                  data-tooltip-place="bottom"
                  data-tooltip-content={buttonDisabled(ProjectLicenseType.EtsExport) ? dataTooltipContent : ""}
                >
                  {isReadbackFromEtsInProgress && (
                    <div>
                      <i className="las la-sync la-spin" />{" "}
                      {state.translate("etsSyncAndExport.runningReadProjectFromETS", "Reading project from ETS")}
                    </div>
                  )}
                  {!isReadbackFromEtsInProgress && (
                    <div>
                      <i className="las la-file-import" />{" "}
                      {itx("etsSyncAndExport.readProjectfromETS", "Read ETS Project")}
                      {readbackBetaBadge}
                    </div>
                  )}
                </button>
                <VersionWarning requiredVersion={ETS_MIN_VERSION.READ_PROJECT_FROM_ETS} />
              </div>
            )}

            <div data-tooltip-id="tv-tooltip" data-tooltip-place="bottom" data-tooltip-content={""}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json" // no itx
                style={{ display: "none" }} // no itx
                onChange={async (ev: any) => {
                  const file = ev.target.files?.[0];
                  if (file) {
                    await processReadbackFile(file);
                    // Reset input so the same file can be selected again
                    ev.target.value = "";
                  }
                }}
              />
              <button
                className="button"
                style={{ width: "100%" }}
                disabled={
                  isReadbackInProgress || !isVersionSufficient(etsVersion, ETS_MIN_VERSION.READ_PROJECT_FROM_ETS)
                }
                onClick={() => handleImportReadbackFile("parameters")}
              >
                {isReadbackFromJsonInProgress && (
                  <div>
                    <i className="las la-sync la-spin" />{" "}
                    {state.translate("etsSyncAndExport.runningImportReadbackFile", "Importing readback JSON file")}
                  </div>
                )}
                {!isReadbackFromJsonInProgress && (
                  <div>
                    <i className="las la-file-upload" style={{ marginRight: "8px" }} />
                    {itx("etsSyncAndExport.importParameterReadbackFile", "Import ETS Parameters JSON")}
                  </div>
                )}
              </button>

              {isUiModeProductAdmin() && (
                <button
                  className="button"
                  style={{ width: "100%" }}
                  disabled={
                    isReadbackInProgress || !isVersionSufficient(etsVersion, ETS_MIN_VERSION.READ_PROJECT_FROM_ETS)
                  }
                  onClick={() => handleImportReadbackFile("full")}
                >
                  {isReadbackFromJsonInProgress && (
                    <div>
                      <i className="las la-sync la-spin" />{" "}
                      {state.translate("etsSyncAndExport.runningImportReadbackFile", "Importing readback JSON file")}
                    </div>
                  )}
                  {!isReadbackFromJsonInProgress && (
                    <div>
                      <i className="las la-file-upload" style={{ marginRight: "8px" }} />
                      {itx("etsSyncAndExport.importReadbackFile", "Import ETS Project JSON")}
                      {readbackBetaBadge}
                    </div>
                  )}
                </button>
              )}
              <VersionWarning requiredVersion={ETS_MIN_VERSION.READ_PROJECT_FROM_ETS} />
            </div>
          </div>
        )}
        <div style={{ borderBottom: "1px solid black", marginBottom: "15px", marginTop: "15px" }}></div>
        <ExportEtsProjectViaServerButton />
        <MoreETSButton />

        {/* ── Device Programming Section ─────────────────────────────── */}
        {isUiModeProductAdmin() && (
          <>
            <div style={{ borderBottom: "1px solid black", marginBottom: "15px", marginTop: "15px" }} />
            <div style={{ marginBottom: "6px", fontWeight: "bold" }}>
              {itx("etsDialog.programDeviceTitle", "Program Device (Download)")}
            </div>
            <div style={{ fontSize: "0.82em", opacity: 0.78, marginBottom: "8px", lineHeight: 1.35 }}>
              {isLoadingBusInfo ? (
                <span
                  title={
                    busInfo?.message ||
                    itx(
                      "etsDialog.busInterfaceInEtsTooltip",
                      "The ETS6 Add-In SDK does not expose a public list/connect API for PC bus interfaces.",
                    )
                  }
                >
                  <i className="las la-sync la-spin" style={{ marginRight: "5px" }} />
                  {itx("etsDialog.loadingBusInfo", "Checking ETS bus connection...")}
                </span>
              ) : (
                <span
                  title={
                    busInfo?.message ||
                    itx(
                      "etsDialog.busInterfaceInEtsTooltip",
                      "The ETS6 Add-In SDK does not expose a public list/connect API for PC bus interfaces.",
                    )
                  }
                >
                  <i className="las la-network-wired" style={{ marginRight: "5px" }} />
                  {busInfo?.lastBusConnectionInfo
                    ? `${itx("etsDialog.activeBusConnection", "Bus connection")}: ${busInfo.lastBusConnectionInfo}`
                    : busInfo?.fallbackConnection
                      ? `${itx("etsDialog.fallbackBusConnection", "Fallback bus connection")}: ${busInfo.fallbackConnection}`
                      : itx(
                          "etsDialog.busInterfaceInEts",
                          "Bus interface selection is controlled by ETS6. The active connection appears here when ETS starts an online operation.",
                        )}
                </span>
              )}
            </div>
            <div
              style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "8px", marginBottom: "6px" }}
            >
              <select
                className="select"
                value={deviceProgramAddress}
                onChange={(ev) => applyDeviceProgramAddress(ev.target.value)}
                disabled={!state.etsIsOnline || isLoadingProjectDevices || isSyncInProgress || isReadbackInProgress}
                style={{ flex: 1, minWidth: "260px" }}
              >
                <option value="">
                  {isLoadingProjectDevices
                    ? itx("etsDialog.loadingProjectDevices", "Loading ETS devices...")
                    : itx("etsDialog.selectProjectDevice", "Select ETS device")}
                </option>
                {(projectDevices.length > 0 ? projectDevices : getFallbackProjectDevices()).map((device) => (
                  <option
                    key={`${device.etsId || device.physicalAddress}_${device.physicalAddress}`}
                    value={device.physicalAddress}
                  >
                    {getProjectDeviceLabel(device)}
                  </option>
                ))}
              </select>
              <button
                className="button"
                style={{ flexShrink: 0, minWidth: "34px", paddingLeft: "8px", paddingRight: "8px" }}
                disabled={
                  !state.etsIsOnline ||
                  isLoadingProjectDevices ||
                  isSyncInProgress ||
                  isReadbackInProgress ||
                  isProgrammingDevice ||
                  !isVersionSufficient(etsVersion, ETS_MIN_VERSION.PROGRAM_DEVICE)
                }
                title={itx("etsDialog.refreshProjectDevices", "Refresh ETS devices")}
                onClick={loadProjectDevicesForProgramming}
              >
                <i className={`las la-sync${isLoadingProjectDevices ? " la-spin" : ""}`} />
              </button>
              <button
                className="button"
                style={{ flexShrink: 0, minWidth: "34px", paddingLeft: "8px", paddingRight: "8px" }}
                disabled={
                  !state.etsIsOnline ||
                  isCheckingDeviceStatus ||
                  isSyncInProgress ||
                  isReadbackInProgress ||
                  isProgrammingDevice ||
                  !deviceProgramAddress.trim() ||
                  Boolean(invalidDeviceProgramAddress) ||
                  !isVersionSufficient(etsVersion, ETS_MIN_VERSION.PROGRAM_DEVICE)
                }
                title={itx(
                  "etsDialog.checkDeviceOnBus",
                  "Check whether the address answers on the selected ETS bus connection",
                )}
                onClick={() => checkSelectedDeviceStatus(deviceProgramAddress, true)}
              >
                <i className={`las la-search${isCheckingDeviceStatus ? " la-spin" : ""}`} />
              </button>
            </div>
            <div
              style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "8px", marginBottom: "6px" }}
            >
              <select
                className="select"
                value={deviceProgramDownloadType}
                onChange={(ev) => {
                  setDeviceProgramDownloadType(ev.target.value as EtsDeviceDownloadType);
                  setDeviceProgramResult(null);
                }}
                style={{ flex: 1, minWidth: "220px" }}
                disabled={!state.etsIsOnline || isSyncInProgress || isReadbackInProgress || isProgrammingDevice}
              >
                <option value="All">{itx("etsDialog.downloadTypeAll", "Download all")}</option>
                <option value="Partial">{itx("etsDialog.downloadTypePartial", "Partial download")}</option>
                <option
                  value="LoadApplicationProgram" // no itx
                >
                  {itx("etsDialog.downloadTypeApplication", "Application")}
                </option>
                <option
                  value="LoadNetworkConfiguration" // no itx
                >
                  {itx("etsDialog.downloadTypeNetwork", "Individual address / network config")}
                </option>
                <option
                  value="LoadNetworkConfigurationBySerialNumber" // no itx
                >
                  {itx("etsDialog.downloadTypeNetworkSerial", "Individual address by serial")}
                </option>
                <option
                  value="OverwriteIndividualAddress" // no itx
                >
                  {itx("etsDialog.downloadTypeOverwriteAddress", "Overwrite individual address")}
                </option>
              </select>
              <input
                type="text"
                value={deviceProgramAddress}
                onChange={(ev) => {
                  setDeviceProgramAddress(ev.target.value);
                  setDeviceProgramResult(null);
                  setDeviceProgramProgress(null);
                  setDeviceStatus(null);
                }}
                onBlur={() => checkSelectedDeviceStatus(deviceProgramAddress, false)}
                placeholder={itx("etsDialog.programDevicePlaceholder", "Physical address, e.g. 1.1.5")}
                disabled={!state.etsIsOnline || isSyncInProgress || isReadbackInProgress || isProgrammingDevice}
                style={{ width: "150px", textAlign: "left" }}
              />
              <button
                className="button"
                style={{ flexShrink: 0 }}
                disabled={programButtonDisabled}
                onClick={handleProgramDevice}
              >
                {isProgrammingDevice ? (
                  <span>
                    <i className="las la-sync la-spin" style={{ marginRight: "6px" }} />
                    {itx("etsDialog.programDeviceBusy", "Programming...")}
                  </span>
                ) : (
                  <span>
                    <i className="las la-download" style={{ marginRight: "6px" }} />
                    {itx("etsDialog.programDeviceButton", "Program Device")}
                  </span>
                )}
              </button>
            </div>
            <div style={{ fontSize: "0.82em", opacity: 0.78, marginBottom: "6px", lineHeight: 1.35 }}>
              {getDownloadTypeHint(deviceProgramDownloadType)}
              {deviceProgramDownloadType === "LoadNetworkConfigurationBySerialNumber" &&
                selectedProjectDevice?.serialNumber && (
                  <span>{` ${itx("etsDialog.serialNumber", "Serial")}: ${selectedProjectDevice.serialNumber}`}</span>
                )}
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "8px",
                marginBottom: "6px",
              }}
            >
              <span style={{ fontSize: "0.84em", opacity: 0.8 }}>
                {itx("etsDialog.programmingModeControl", "Programming mode")}
              </span>
              <button
                className="button"
                style={{ flexShrink: 0 }}
                disabled={programmingModeButtonDisabled}
                onClick={() => handleSetProgrammingMode(true)}
                title={itx(
                  "etsDialog.setProgrammingModeTitle",
                  "Set programming mode on the selected KNX device via the ETS bus connection",
                )}
              >
                <i
                  className={`las ${isSettingProgrammingMode ? "la-sync la-spin" : "la-bolt"}`}
                  style={{ marginRight: "5px" }}
                />
                {itx("etsDialog.setProgrammingMode", "Set mode")}
              </button>
              <button
                className="button"
                style={{ flexShrink: 0 }}
                disabled={programmingModeButtonDisabled}
                onClick={() => handleSetProgrammingMode(false)}
                title={itx(
                  "etsDialog.clearProgrammingModeTitle",
                  "Clear programming mode on the selected KNX device via the ETS bus connection",
                )}
              >
                <i className="las la-power-off" style={{ marginRight: "5px" }} />
                {itx("etsDialog.clearProgrammingMode", "Clear mode")}
              </button>
            </div>
            {deviceProgramDownloadType === "OverwriteIndividualAddress" && (
              <div
                style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "8px", marginBottom: "6px" }}
              >
                <span style={{ fontSize: "0.85em", minWidth: "150px" }}>
                  {itx("etsDialog.currentDeviceAddress", "Current device address")}
                </span>
                <input
                  type="text"
                  value={deviceProgramCurrentAddress}
                  onChange={(ev) => {
                    setDeviceProgramCurrentAddress(ev.target.value);
                    setDeviceProgramResult(null);
                  }}
                  placeholder={itx("etsDialog.currentDeviceAddressPlaceholder", "Known current address")}
                  disabled={!state.etsIsOnline || isSyncInProgress || isReadbackInProgress || isProgrammingDevice}
                  style={{ width: "150px", textAlign: "left" }}
                />
              </div>
            )}
            {(invalidDeviceProgramAddress || invalidCurrentProgramAddress) && (
              <div style={{ color: "red", fontSize: "0.82em", marginBottom: "6px" }}>
                <i className="las la-exclamation-triangle" style={{ marginRight: "5px" }} />
                {invalidDeviceProgramAddress || invalidCurrentProgramAddress}
              </div>
            )}
            {deviceStatus && (
              <div
                style={{
                  padding: "6px 10px",
                  borderRadius: "4px",
                  marginTop: "4px",
                  marginBottom: "4px",
                  fontSize: "0.84em",
                  background:
                    deviceStatus.existsInProject === false
                      ? "rgba(200,0,0,0.10)" // no itx
                      : "rgba(0,0,0,0.05)", // no itx
                  border: deviceStatus.existsInProject === false ? "1px solid red" : "1px solid rgba(0,0,0,0.15)",
                }}
              >
                <i
                  className={deviceStatus.existsInProject === false ? "las la-times-circle" : "las la-info-circle"}
                  style={{ marginRight: "6px" }}
                />
                {deviceStatus.message}
                {deviceStatus.busProbePerformed && (
                  <span style={{ marginLeft: "8px" }}>
                    {deviceStatus.existsOnBus
                      ? itx("etsDialog.addressAnswersOnBus", "Address answers on bus.")
                      : itx("etsDialog.addressMissingOnBus", "No device answered at this address on the selected bus.")}
                  </span>
                )}
              </div>
            )}
            <VersionWarning requiredVersion={ETS_MIN_VERSION.PROGRAM_DEVICE} />
            {/* ── Queued confirmation ── only shown briefly after the download is sent ── */}
            {deviceProgramProgress &&
              deviceProgramProgress.state === "Queued" &&
              !deviceProgramProgress.requiresProgrammingMode && (
                <div
                  style={{
                    padding: "6px 10px",
                    borderRadius: "4px",
                    marginTop: "4px",
                    fontSize: "0.86em",
                    background: "rgba(0,0,0,0.05)",
                    border: "1px solid rgba(0,0,0,0.15)",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <i className="las la-check-circle" style={{ color: "var(--knxColorGreen, #4caf50)" }} />
                  <span>
                    {deviceProgramProgress.message ||
                      itx("etsDialog.programDeviceAddedToQueue", "Download added to queue.")}
                  </span>
                </div>
              )}
            {/* ── Programming mode prompt ── */}
            {deviceProgramProgress?.requiresProgrammingMode && (
              <div
                style={{
                  padding: "7px 10px",
                  borderRadius: "4px",
                  marginTop: "4px",
                  fontSize: "0.86em",
                  background: "rgba(255,165,0,0.14)",
                  border: "1px solid orange",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <i className="las la-hand-pointer" />
                  <span style={{ flex: 1 }}>
                    {deviceProgramProgress.message ||
                      itx(
                        "etsDialog.pressProgrammingButton",
                        "ETS is waiting for programming mode. Set programming mode from this app, or press the physical programming button if remote mode is not possible.",
                      )}
                  </span>
                  <button
                    className="button"
                    style={{ flexShrink: 0, paddingLeft: "8px", paddingRight: "8px" }}
                    disabled={isSettingProgrammingMode}
                    onClick={() => handleSetProgrammingMode(true)}
                  >
                    <i
                      className={`las ${isSettingProgrammingMode ? "la-sync la-spin" : "la-bolt"}`}
                      style={{ marginRight: "5px" }}
                    />
                    {itx("etsDialog.tryRemoteProgrammingMode", "Set mode")}
                  </button>
                </div>
              </div>
            )}
            {/* ── Already running indicator ── */}
            {isProgrammingDevice && !deviceProgramProgress?.requiresProgrammingMode && (
              <div
                style={{
                  padding: "6px 10px",
                  borderRadius: "4px",
                  marginTop: "4px",
                  fontSize: "0.86em",
                  background: "rgba(0,100,255,0.07)",
                  border: "1px solid rgba(0,100,255,0.25)",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <i className="las la-sync la-spin" style={{ flexShrink: 0 }} />
                <span>
                  {itx("etsDialog.downloadOperationRunning", "Download operation running")}
                  {deviceProgramAddress.trim() ? ` — ${deviceProgramAddress.trim()}` : ""}
                </span>
              </div>
            )}
            {deviceProgramResult && (
              <div
                style={{
                  padding: "6px 10px",
                  borderRadius: "4px",
                  marginTop: "4px",
                  fontSize: "0.88em",
                  background:
                    deviceProgramResult.status === "OK"
                      ? "rgba(0,200,0,0.12)" // no itx
                      : deviceProgramResult.status === "INFO"
                        ? "rgba(0,100,255,0.10)" // no itx
                        : "rgba(200,0,0,0.12)", // no itx
                  border:
                    deviceProgramResult.status === "OK"
                      ? "1px solid green"
                      : deviceProgramResult.status === "INFO"
                        ? "1px solid rgba(0,100,255,0.4)"
                        : "1px solid red",
                }}
              >
                <i
                  className={
                    deviceProgramResult.status === "OK"
                      ? "las la-check-circle" // no itx
                      : deviceProgramResult.status === "INFO"
                        ? "las la-info-circle"
                        : "las la-times-circle"
                  }
                  style={{ marginRight: "6px" }}
                />
                {deviceProgramResult.message}
              </div>
            )}
          </>
        )}
      </div>
    </BaseDialog>
  );
}
